// Port of getAnswer (chart.coffee:131-193) + getTotalPickedUpAnswer (lines 195-258).

import { formatAmounts, pluralize } from '@aytm/app/stats/lib/util/helpers'

import { getFrequencyAnswer } from '@aytm/app/stats/page/question-views/ec/components/chart/lib/frequency'
import {
  isTraitExcluded,
  SUBSCRIBE_RATE_MODES,
  viewModeCategoryOf
} from '@aytm/app/stats/page/question-views/ec/components/chart/lib/viewOptions'

const isAnswerKey = key => key[0] === 'a' && !Number.isNaN(Number(key.slice(1)))

const MONEY_MODES = ['pAmount', 'subscribeSpend']
const TWO_DECIMAL_MODES = ['pAmount', 'pVolume', 'subscribeSpend']
const ONE_DECIMAL_MODES = ['bannerCtr', 'bannerFunnelPopup', 'bannerFunnelCart', 'bannerAvgViewToClick']

// `amount` is the raw number, NOT the formatted string — formatAmounts returns a string and
// `'1' !== 1` would make every singular read as plural ("1 seconds").
const calcUnitLabel = (viewMode, amount) => {
  // amount arrives as a formatted string — coerce like the sh twin does
  if (['attention', 'time', 'timeToFirstClick'].includes(viewMode)) {
    return pluralize(Number(amount), 'second', 'seconds')
  }
  if (viewMode === 'amount') {
    return pluralize(Number(amount), 'respondent', 'respondents')
  }
  return ' '
}

const formatAmount = (raw, viewMode) => {
  if (TWO_DECIMAL_MODES.includes(viewMode)) {
    // Defensive cast — API may return number or numeric string.
    // Coffee calls `.toFixed` directly (chart.coffee:152) and would crash on string;
    // Number() cast keeps parity for numbers and protects against string inputs.
    return Number(raw).toFixed(2)
  }
  if (ONE_DECIMAL_MODES.includes(viewMode) || SUBSCRIBE_RATE_MODES.includes(viewMode)) {
    return Number(raw).toFixed(1)
  }
  return formatAmounts({ amount: raw }, 'amount', 1)
}

// Banner metrics live under answer.banners, with the two funnel rates one level deeper.
const getBannerAmount = ({ answer, ak, viewMode }) => {
  const bannerData = answer?.banners?.[ak] || {}
  const funnelData = answer?.banners?.funnel?.[ak] || {}

  switch (viewMode) {
    case 'bannerImpressions':
      return bannerData.impressions || 0
    case 'bannerClicks':
      return bannerData.clicks || 0
    case 'bannerCtr':
      return (bannerData.ctr || 0) * 100
    case 'bannerFunnelPopup':
      return (funnelData.clickToPopupRate || 0) * 100
    case 'bannerFunnelCart':
      return (funnelData.clickToCartRate || 0) * 100
    case 'bannerAvgViewToClick':
      return bannerData.avgViewToClick || 0
    default:
      return 0
  }
}

// Subscribe metrics live under answer.subscribe; every one except spend is a 0..1 rate.
const getSubscribeAmount = ({ answer, ak, viewMode }) => {
  const subData = answer?.subscribe?.[ak] || {}
  if (viewMode === 'subscribeSpend') return subData.subscribeSpend || 0
  return (subData[viewMode] || 0) * 100
}

const getRawAmount = ({ answer, av, ak, viewMode }) => {
  switch (viewModeCategoryOf(viewMode)) {
    case 'banner':
      return getBannerAmount({ answer, ak, viewMode })
    case 'subscribe':
      return getSubscribeAmount({ answer, ak, viewMode })
    default:
      return av[viewMode] || 0
  }
}

const getTotalPickedUpAnswer = ({ answer, answerVariants, trait, traitName, traitValues }) => {
  // Port of chart.coffee:195-258. Result keys are `bN` (NOT `aN`).
  // Each batch has 3 sub-variants (no trait) or 3*traitValues.length (with trait).
  //
  // IMPORTANT: `purchasedPercents` / `pickedUpPercents` are computed in the outer scope
  // BEFORE entering the trait branch — the non-trait branch reads those outer values
  // (coffee lines 208-209). Do not move the scope!

  const total = answer?.total || 0
  const hcAnswers = {}

  for (const av of answerVariants || []) {
    const position = av.position
    const ak = `a${position}`
    const bk = `b${position}`

    const akAnswer = answer?.[ak] || {}

    // Outer scope — used by the non-trait branch below.
    const purchasedPercents = akAnswer.percents || 0
    const pickedUpPercents = akAnswer.pickedUpPercents || 0

    const bAnswer = {
      amount: purchasedPercents + pickedUpPercents,
      total
    }

    if (trait) {
      const avTrait = akAnswer[traitName] || {}
      ;(traitValues || []).forEach((traitValue, idx) => {
        const traitAnswer = avTrait[traitValue.id] || {}
        const traitAnswerTotal = answer?.[traitName]?.[traitValue.id]?.total || 0

        // Purchased
        const purchasedAmount = traitAnswer.amount || 0
        const traitPurchasedPercents = (purchasedAmount / total) * 100
        const purchaseKey = `a${idx * 3 + 1}`
        bAnswer[purchaseKey] = { amount: purchasedAmount, percents: traitPurchasedPercents }

        // Picked up only
        const totalPickedUp = traitAnswer.totalPickedUp || 0
        const traitPickedUpPercents = traitAnswer.pickedUpPercents || (totalPickedUp / total) * 100
        const pickedUpKey = `a${idx * 3 + 2}`
        bAnswer[pickedUpKey] = { amount: totalPickedUp, percents: traitPickedUpPercents }

        // No action — coffee uses `||` fallback (chart.coffee:234, 235), so `0` falls through.
        // Keep `||` for strict parity (do NOT use `??` — would change behavior for `totalNoAction: 0`).
        // If `answer[traitName][traitValue.id].total` is undefined → buggy NaN (matches coffee).
        const totalNoAction = traitAnswer.totalNoAction || traitAnswerTotal - purchasedAmount - totalPickedUp
        const noActionPercents = traitAnswer.noActionPercents || (totalNoAction / total) * 100
        const noActionKey = `a${idx * 3 + 3}`
        bAnswer[noActionKey] = { amount: totalNoAction, percents: noActionPercents }
      })
    } else {
      // Non-trait branch — reads the outer purchasedPercents / pickedUpPercents.
      const purchasedAmount = akAnswer.amount || 0
      const totalPickedUp = akAnswer.totalPickedUp || 0
      const totalNoAction = akAnswer.totalNoAction || 0
      const noActionPercents = akAnswer.noActionPercents || 0

      bAnswer.a1 = { amount: purchasedAmount, percents: purchasedPercents }
      bAnswer.a2 = { amount: totalPickedUp, percents: pickedUpPercents }
      bAnswer.a3 = { amount: totalNoAction, percents: noActionPercents }
    }

    hcAnswers[bk] = bAnswer
  }

  return hcAnswers
}

export const getAnswer = ({ answer, answerVariants, viewMode, priceTemplate, trait, traitName, traitValues }) => {
  const total = answer?.total || 0
  if (viewMode === 'totalPickedUp') {
    return { total, ...getTotalPickedUpAnswer({ answer, answerVariants, trait, traitName, traitValues }) }
  }
  if (viewMode === 'frequency') {
    return { total, ...getFrequencyAnswer({ answer, answerVariants }) }
  }

  const hcAnswers = { total }

  for (const ak of Object.keys(answer || {})) {
    if (!isAnswerKey(ak)) continue
    const av = answer[ak]
    const value = {}

    const rawAmount = getRawAmount({ answer, av, ak, viewMode })
    value.amount = formatAmount(rawAmount, viewMode)
    // shown rounded, sorted on the raw value the table sorts by, so values that round alike keep its order
    value.sortValue = Number(rawAmount) || 0
    if (viewMode === 'amount') value.percents = av.percents || 0
    value.unitLabel = calcUnitLabel(viewMode, rawAmount)

    if (MONEY_MODES.includes(viewMode) && priceTemplate) {
      value.unitTemplate = priceTemplate.replace(/{{price}}/gi, '{{unit}}')
    }
    if (viewMode === 'amount' && av.errLO && av.errHI) {
      value.errLO = av.errLO
      value.errHI = av.errHI
    }

    // Banner/subscribe payloads carry no per-trait breakdown — coffee skipped the split entirely
    // rather than rendering a row of zeroed trait bars.
    if (trait && !isTraitExcluded(viewMode)) {
      const traitBlock = {}
      const avTrait = av[traitName] || {}
      for (const tk of Object.keys(avTrait)) {
        const tv = avTrait[tk]
        const traitValue = {
          amount: formatAmount(tv[viewMode] || 0, viewMode),
          unitLabel: value.unitLabel
        }
        if (viewMode === 'amount') traitValue.percents = tv.percents || 0
        if (viewMode === 'pAmount' && priceTemplate) {
          traitValue.unitTemplate = priceTemplate.replace(/{{price}}/gi, '{{unit}}')
        }
        traitBlock[tk] = traitValue
      }
      value[traitName] = traitBlock
    }

    hcAnswers[ak] = value
  }

  return hcAnswers
}
